import React, { useState, useEffect } from 'react'
import { supabase } from '../../supabaseClient'
import { useAuth } from '../../contexts/AuthContext'
import ImageGallery from '../../components/ImageGallery'

/**
 * ManageCompleteLureSubmissions Component
 * 
 * Admin interface for reviewing and approving/rejecting 
 * complete lure package submissions
 * 
 * TEMPORARY: Removed database admin check that was failing
 */
function ManageCompleteLureSubmissions() {
  const { user } = useAuth()
  
  const [submissions, setSubmissions] = useState([])
  const [loading, setLoading] = useState(true)
  const [processing, setProcessing] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [submissionImages, setSubmissionImages] = useState({}) // { submissionId: [images] }
  
  // Modal state for rejection
  const [showRejectModal, setShowRejectModal] = useState(false)
  const [rejectingSubmission, setRejectingSubmission] = useState(null)
  const [rejectNotes, setRejectNotes] = useState('')

  // ============================================
  // LOAD SUBMISSIONS
  // ============================================
  
  useEffect(() => {
    // Fetch submissions immediately
    // Admin check is handled by AdminLayout component
    fetchSubmissions()
  }, [])

  const fetchSubmissions = async () => {
    try {
      setLoading(true)
      setError('')
      
      console.log('=== FETCHING SUBMISSIONS (No-Join Method) ===')
      
      // Get submissions
      const { data: submissions, error: subError } = await supabase
        .from('complete_lure_submissions')
        .select('*')
        .eq('status', 'pending')
        .order('created_at', { ascending: true })
      
      if (subError) {
        console.error('Error fetching submissions:', subError)
        throw subError
      }
      
      console.log('Found submissions:', submissions?.length || 0)
      
      if (!submissions || submissions.length === 0) {
        setSubmissions([])
        setLoading(false)
        return
      }
      
      // Get all related data separately
      const userIds = [...new Set(submissions.map(s => s.user_id))]
      const brandIds = [...new Set(submissions.map(s => s.existing_brand_id).filter(Boolean))]
      const typeIds = [...new Set(submissions.map(s => s.lure_type_id))]
      const subtypeIds = [...new Set(submissions.map(s => s.lure_subtype_id).filter(Boolean))]
      
      console.log('Fetching related data...')
      console.log('User IDs:', userIds)
      console.log('Brand IDs:', brandIds)
      console.log('Type IDs:', typeIds)
      console.log('Subtype IDs:', subtypeIds)
      
      // Fetch users
      const { data: users } = await supabase
        .from('users')
        .select('id, username, email')
        .in('id', userIds)
      
      // Fetch brands
      const { data: brands } = brandIds.length > 0 
        ? await supabase.from('brands').select('brand_id, brand_name').in('brand_id', brandIds)
        : { data: [] }
      
      // Fetch lure types
      const { data: types } = await supabase
        .from('lure_types')
        .select('type_id, type_name')
        .in('type_id', typeIds)
      
      // Fetch subtypes
      const { data: subtypes } = subtypeIds.length > 0
        ? await supabase.from('lure_subtypes').select('subtype_id, subtype_name').in('subtype_id', subtypeIds)
        : { data: [] }
      
      console.log('Related data fetched:', {
        users: users?.length,
        brands: brands?.length,
        types: types?.length,
        subtypes: subtypes?.length
      })
      
      // Map related data to submissions
      const enrichedSubmissions = submissions.map(sub => ({
        ...sub,
        users: users?.find(u => u.id === sub.user_id),
        brands: brands?.find(b => b.brand_id === sub.existing_brand_id),
        lure_types: types?.find(t => t.type_id === sub.lure_type_id),
        lure_subtypes: subtypes?.find(st => st.subtype_id === sub.lure_subtype_id)
      }))
      
      console.log('Enriched submissions:', enrichedSubmissions)
      
      setSubmissions(enrichedSubmissions)
      
      // Fetch images for each submission
      await fetchSubmissionImages(enrichedSubmissions)
      
      setLoading(false)
    } catch (error) {
      console.error('Error fetching submissions:', error)
      setError('Failed to load submissions')
      setLoading(false)
    }
  }

  const fetchSubmissionImages = async (submissions) => {
    try {
      const submissionIds = submissions.map(s => s.submission_id)
      
      if (submissionIds.length === 0) return
      
      const { data: images, error: imgError } = await supabase
        .from('submission_images')
        .select('*')
        .in('submission_id', submissionIds)
        .order('upload_order', { ascending: true })
      
      if (imgError) {
        console.error('Error fetching submission images:', imgError)
        return
      }
      
      // Group images by submission_id
      const imagesBySubmission = {}
      images?.forEach(img => {
        if (!imagesBySubmission[img.submission_id]) {
          imagesBySubmission[img.submission_id] = []
        }
        imagesBySubmission[img.submission_id].push(img)
      })
      
      console.log('Images by submission:', imagesBySubmission)
      setSubmissionImages(imagesBySubmission)
      
    } catch (error) {
      console.error('Error in fetchSubmissionImages:', error)
    }
  }

  // ============================================
  // APPROVAL / REJECTION HANDLERS
  // ============================================
  
  const handleApprove = async (submissionId) => {
    if (!window.confirm('Approve this complete lure package? This will create all database entries.')) {
      return
    }
    
    try {
      setProcessing(true)
      setError('')
      setSuccess('')
      
      console.log('=== APPROVING SUBMISSION ===')
      console.log('Submission ID:', submissionId)
      
      // Call approval function
      const { data, error } = await supabase.rpc('approve_complete_lure_submission', {
          p_submission_id: submissionId,
          p_admin_id: user.id,
          p_admin_notes: 'Approved'
        })
      
      if (error) {
        console.error('Approval error:', error)
        throw error
      }
      
      // Check if the function returned success
      if (!data.success) {
        throw new Error(data.error || 'Approval failed')
      }
      
      setSuccess(`✅ Submission approved! Brand #${data.brand_id}, Lure #${data.lure_id}, Color: ${data.color_name}, Variant #${data.variant_id}`)
      
      // Scroll to success banner
      setTimeout(() => {
        const feedbackElement = document.querySelector('.contextual-feedback')
        if (feedbackElement) {
          feedbackElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
      }, 100)
      
      // Refresh list
      await fetchSubmissions()
      
    } catch (error) {
      console.error('Error approving submission:', error)
      setError(error.message || 'Failed to approve submission')
      // Scroll to error banner
      setTimeout(() => {
        const feedbackElement = document.querySelector('.contextual-feedback')
        if (feedbackElement) {
          feedbackElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
      }, 100)
    } finally {
      setProcessing(false)
    }
  }

  const handleRejectClick = (submission) => {
    setRejectingSubmission(submission)
    setShowRejectModal(true)
    setRejectNotes('')
  }

  const handleRejectConfirm = async () => {
    if (!rejectingSubmission) return
    
    try {
      setProcessing(true)
      setError('')
      setSuccess('')
      
      console.log('=== REJECTING SUBMISSION ===')
      console.log('Submission ID:', rejectingSubmission.submission_id)
      console.log('Notes:', rejectNotes)
      
      const { error } = await supabase
        .from('complete_lure_submissions')
        .update({
          status: 'rejected',
          admin_notes: rejectNotes || 'Rejected without notes',
          reviewed_at: new Date().toISOString()
        })
        .eq('submission_id', rejectingSubmission.submission_id)
      
      if (error) throw error
      
      setSuccess(`Submission rejected: ${rejectingSubmission.lure_name}`)
      setShowRejectModal(false)
      setRejectingSubmission(null)
      
      // Scroll to success banner
      setTimeout(() => {
        const feedbackElement = document.querySelector('.contextual-feedback')
        if (feedbackElement) {
          feedbackElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
      }, 100)
      
      // Refresh list
      await fetchSubmissions()
      
    } catch (error) {
      console.error('Error rejecting submission:', error)
      setError(error.message || 'Failed to reject submission')
      // Scroll to error banner
      setTimeout(() => {
        const feedbackElement = document.querySelector('.contextual-feedback')
        if (feedbackElement) {
          feedbackElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
      }, 100)
    } finally {
      setProcessing(false)
    }
  }

  // ============================================
  // RENDER
  // ============================================

  if (loading) {
    return (
      <div className="manage-submissions">
        <h1>Manage Complete Lure Submissions</h1>
        <div style={{
          textAlign: 'center',
          padding: '3rem',
          color: '#64748b'
        }}>
          Loading submissions...
        </div>
      </div>
    )
  }

  return (
    <div className="manage-submissions">
      <h1>Manage Complete Lure Submissions</h1>
      <p className="subtitle">Review and approve user-submitted lure packages</p>

      {/* Submissions Count */}
      <div style={{
        background: '#f8fafc',
        padding: '1rem',
        borderRadius: '8px',
        marginBottom: '2rem',
        border: '1px solid #e2e8f0'
      }}>
        <p style={{ margin: 0, color: '#475569' }}>
          <strong>{submissions.length}</strong> pending submission{submissions.length !== 1 ? 's' : ''}
        </p>
      </div>

      {/* CONTEXTUAL FEEDBACK - Positioned right before submissions list */}
      <div className="contextual-feedback">
        {/* Success Message */}
        {success && (
          <div className="contextual-banner contextual-banner-success">
            <div className="contextual-banner-content">
              <div className="contextual-banner-icon">✅</div>
              <div className="contextual-banner-text">
                <h3>Success</h3>
                <p>{success}</p>
              </div>
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="contextual-banner contextual-banner-error">
            <div className="contextual-banner-content">
              <div className="contextual-banner-icon">⚠️</div>
              <div className="contextual-banner-text">
                <h3>Error</h3>
                <p>{error}</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Submissions List */}
      {submissions.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: '3rem',
          background: '#f8fafc',
          borderRadius: '12px',
          border: '2px dashed #cbd5e1'
        }}>
          <p style={{ fontSize: '1.2rem', color: '#64748b', margin: 0 }}>
            🎉 No pending submissions
          </p>
          <p style={{ color: '#94a3b8', marginTop: '0.5rem' }}>
            All caught up!
          </p>
        </div>
      ) : (
        <div className="submissions-list">
          {submissions.map((submission) => (
            <div key={submission.submission_id} className="submission-card">
              {/* Submission Header */}
              <div className="submission-header">
                <div>
                  <h3 style={{ margin: '0 0 0.5rem 0', color: '#1e293b' }}>
                    {submission.lure_name}
                  </h3>
                  <p style={{ margin: 0, color: '#64748b', fontSize: '0.9rem' }}>
                    Submitted by <strong>{submission.users?.username || 'Unknown'}</strong> on{' '}
                    {new Date(submission.created_at).toLocaleDateString()}
                  </p>
                </div>
                <div className="submission-actions">
                  <button
                    onClick={() => handleApprove(submission.submission_id)}
                    disabled={processing}
                    className="btn-approve"
                  >
                    ✅ Approve
                  </button>
                  <button
                    onClick={() => handleRejectClick(submission)}
                    disabled={processing}
                    className="btn-reject"
                  >
                    ❌ Reject
                  </button>
                </div>
              </div>
              
              {submissionImages[submission.submission_id] && submissionImages[submission.submission_id].length > 0 && (
                <div className="submission-images-section">
                  <h4>📸 Submitted Images ({submissionImages[submission.submission_id].length})</h4>
                  <ImageGallery 
                    images={submissionImages[submission.submission_id]}
                    onDelete={null} // Admins can't delete yet, we'll add this later
                  />
                </div>
              )}

              {/* Submission Details */}
              <div className="submission-details">
                <div className="detail-section">
                  <h4>Brand</h4>
                  <p>
                    {submission.use_existing_brand
                      ? submission.brands?.brand_name || 'Unknown'
                      : `NEW: ${submission.new_brand_name}`}
                  </p>
                  {!submission.use_existing_brand && submission.new_brand_country && (
                    <p className="detail-sub">Country: {submission.new_brand_country}</p>
                  )}
                </div>

                <div className="detail-section">
                  <h4>Lure Details</h4>
                  <p><strong>Type:</strong> {submission.lure_types?.type_name}</p>
                  {submission.lure_subtypes && (
                    <p><strong>Subtype:</strong> {submission.lure_subtypes.subtype_name}</p>
                  )}
                  {submission.lure_weight && <p><strong>Weight:</strong> {submission.lure_weight}</p>}
                  {submission.lure_length && <p><strong>Length:</strong> {submission.lure_length}</p>}
                  {submission.lure_depth_range && <p><strong>Depth:</strong> {submission.lure_depth_range}</p>}
                </div>

                <div className="detail-section">
                  <h4>Color</h4>
                  <p><strong>{submission.color_name}</strong></p>
                  {submission.is_limited_edition && <span className="badge">Limited Edition</span>}
                  {submission.is_discontinued && <span className="badge badge-discontinued">Discontinued</span>}
                </div>

                {submission.variant_name && (
                  <div className="detail-section">
                    <h4>Variant</h4>
                    <p>{submission.variant_name}</p>
                  </div>
                )}
              </div>

              {/* Description Sections */}
              {(submission.lure_description || submission.color_description || submission.variant_notes) && (
                <div className="submission-descriptions">
                  {submission.lure_description && (
                    <div>
                      <h5>Lure Description:</h5>
                      <p>{submission.lure_description}</p>
                    </div>
                  )}
                  {submission.color_description && (
                    <div>
                      <h5>Color Description:</h5>
                      <p>{submission.color_description}</p>
                    </div>
                  )}
                  {submission.variant_notes && (
                    <div>
                      <h5>Variant Notes:</h5>
                      <p>{submission.variant_notes}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="modal-overlay" onClick={() => setShowRejectModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Reject Submission</h2>
            <p>
              Rejecting: <strong>{rejectingSubmission?.lure_name}</strong>
            </p>
            <label htmlFor="rejectNotes">Admin Notes (Optional)</label>
            <textarea
              id="rejectNotes"
              value={rejectNotes}
              onChange={(e) => setRejectNotes(e.target.value)}
              placeholder="Reason for rejection..."
              rows="4"
              className="form-textarea"
            />
            <div className="modal-actions">
              <button
                onClick={() => setShowRejectModal(false)}
                disabled={processing}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleRejectConfirm}
                disabled={processing}
                className="btn-reject"
              >
                {processing ? 'Rejecting...' : 'Confirm Rejection'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ManageCompleteLureSubmissions